import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiErrorMessage } from '../api/client';
import StatusPill from '../components/StatusPill';

interface Row {
  student_id: string;
  short_id: string | null;
  first_name: string;
  last_name: string;
  grade_label: string;
  date_of_birth: string;
  registry_id: string | null;
  guardian_name: string;
  guardian_phone: string;
  guardian_email: string;
  compliance_status: string;
  deficiency: string | null;
  created_at: string | null;
}

type SortOrder = 'asc' | 'desc';

// Built once. Shows the browser's zone (Florida spans Eastern and Central).
const addedAtFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

/** Guardian cell — plan §3.6: name, else email, else phone, else "No contact info". */
const GuardianCell: React.FC<{ r: Row }> = ({ r }) => {
  const primary = r.guardian_name || r.guardian_email || r.guardian_phone;
  if (!primary) {
    return <span className="italic text-slate-400">No contact info</span>;
  }
  const secondary = [r.guardian_email, r.guardian_phone]
    .filter((v) => v && v !== primary)
    .join(' · ');
  return (
    <>
      <div className="break-all">{primary}</div>
      {secondary && <div className="break-all text-xs text-slate-400">{secondary}</div>}
    </>
  );
};

const DirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [gradeBand, setGradeBand] = useState('');
  const [status, setStatus] = useState('');
  // Sort is a view preference, not a filter: "Clear Filters" keeps it.
  // Only "Record added at" is sortable today; add columns explicitly.
  const sortBy = 'created_at' as const;
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const load = useCallback(
    async (targetPage = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          page: targetPage,
          limit: 25,
          sort_by: sortBy,
          sort_order: sortOrder,
        };
        if (search) params.search = search;
        if (gradeBand) params.grade_band = gradeBand;
        if (status) params.compliance_status = status;
        const res = await api.get('/api/admin/students', { params });
        setRows(res.data.items as Row[]);
        setTotal(res.data.total);
        setPages(res.data.pages || 1);
        setPage(res.data.page || targetPage);
      } catch (err) {
        setError(apiErrorMessage(err, 'Search failed.'));
      } finally {
        setLoading(false);
      }
    },
    [search, gradeBand, status, sortBy, sortOrder]
  );

  // Initial load and every sort change (back to page 1, no extra click).
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(1);
  };

  const clearFilters = () => {
    setSearch('');
    setGradeBand('');
    setStatus('');
    setTimeout(() => load(1), 0);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">Student Directory</h1>

      <form onSubmit={onSearch} className="rounded-xl bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search student / guardian name, email, phone, DOB, STU-XXXX, reg ID"
            aria-label="Search the student directory"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm md:col-span-2"
          />
          <select
            value={gradeBand}
            onChange={(e) => setGradeBand(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">All Grade Bands</option>
            <option value="K">K — Kindergarten</option>
            <option value="1-6">Grades 1–6</option>
            <option value="7-12">Grades 7–12</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Any Status</option>
            <option value="COMPLIANT">Compliant</option>
            <option value="NON_COMPLIANT">Non-Compliant</option>
            <option value="EXEMPT">Exempt</option>
            <option value="PENDING_REVIEW">Pending Review</option>
          </select>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Search Directory
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Clear Filters
          </button>
        </div>
      </form>

      {error && <div className="text-red-600">{error}</div>}

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Student Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Grade</th>
              <th className="px-4 py-3">DOB</th>
              <th className="px-4 py-3">Guardian</th>
              <th className="px-4 py-3">DH 680 Status</th>
              <th
                className="px-4 py-3 text-right"
                aria-sort={sortOrder === 'desc' ? 'descending' : 'ascending'}
              >
                <button
                  type="button"
                  onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-900"
                  title={
                    sortOrder === 'desc'
                      ? 'Newest first — click for oldest first'
                      : 'Oldest first — click for newest first'
                  }
                >
                  Record added at
                  <span aria-hidden="true" className="font-bold text-blue-600">
                    {sortOrder === 'desc' ? '↓' : '↑'}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                  No students found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.student_id}
                  onClick={() => navigate(`/students/${r.short_id || r.student_id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {r.last_name}, {r.first_name}
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-xs text-blue-700">
                    {r.short_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.grade_label}</td>
                  <td className="px-4 py-3 text-slate-600">{r.date_of_birth}</td>
                  <td className="max-w-xs px-4 py-3 text-slate-600">
                    <GuardianCell r={r} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={r.compliance_status} />
                    {r.deficiency && (
                      <div className="text-xs text-slate-400">{r.deficiency}</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-xs text-slate-500">
                    {r.created_at ? (
                      <time dateTime={r.created_at}>
                        {addedAtFmt.format(new Date(r.created_at))}
                      </time>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{total} record{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => load(page - 1)}
            className="rounded-md border border-slate-300 px-3 py-1 disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            Page {page} / {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => load(page + 1)}
            className="rounded-md border border-slate-300 px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectoryPage;
